import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import {
  FaCheckCircle, FaChevronDown, FaChevronUp, FaEdit, FaEnvelope, FaListAlt,
  FaPaperPlane, FaSpinner, FaTimes, FaUser, FaUsers,
} from 'react-icons/fa';
import { MdSubject } from 'react-icons/md';
import Button from 'components/common/Button';
import { AgGridTable } from 'components/common';
import RecipientSelect from 'components/common/select/RecipientSelect';
import TinymceEditor from 'components/common/TinymceEditor';
import { getPlanningEmailConfig } from 'redux/crm/releaseForecastSlice';
import { recipientGreeting } from '../components/helpers';

const currentPeriodStr = () => dayjs().format('MMM-YYYY');

const buildingNames = (project) =>
  (project?.buildings || []).map((building) => building.name).filter(Boolean).join(', ');

// Every release month the project's buildings fall into, e.g. "Aug-26, Sep-26".
const releaseMonths = (project) => {
  const months = (project?.buildings || []).flatMap((building) => building.releaseMonths || []);
  return [...new Set(months)].join(', ');
};

// Text-only, whitespace-insensitive compare — TinyMCE re-serialises whatever we
// hand it (tags reordered, entities rewritten), so only the words are reliable.
const squash = (html) => String(html || '').replace(/<[^>]*>/g, '').replace(/\s+|&nbsp;/g, '').toLowerCase();

const buildDefaultBody = ({ period, recipient, sender, url }) => `
<p>Dear <strong>${recipient}</strong>,</p>
<p>The production forecast for <strong>${period}</strong> has been released and is ready for review.</p>
<p>Please click the link below to access the complete forecast: <a href="${url}" target="_blank">View Forecast</a></p>
<p>Best Regards,<br>${sender}</p>`;

const FieldLabel = ({ icon: Icon, children }) => (
  <div className="tw-flex tw-w-20 tw-shrink-0 tw-items-center tw-gap-1.5">
    <Icon size={11} className="tw-text-slate-400" />
    <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-500">{children}</span>
  </div>
);

FieldLabel.propTypes = {
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
};

const ProductionReleaseConfirmDialog = ({ record, defaultPeriod, onConfirm, onCancel }) => {
  const dispatch = useDispatch();
  // The period is whatever the page is already filtered on — released as-is, so
  // the dialog shows it rather than offering a second, competing filter.
  const confirmPeriod = record?.period || defaultPeriod || currentPeriodStr();
  const projects = useMemo(
    () => (record?.projects || []).map((project) => ({ ...project, buildings: project.buildings || [] })),
    [record]
  );
  const projectCount = projects.length || record?.recordCount || 0;
  const buildingCount = projects.reduce((total, project) => total + project.buildings.length, 0);

  // One row per project: its buildings are joined onto the same line so the list
  // stays compact, with the full text on hover for long ones.
  const columnDefs = useMemo(() => [
    {
      headerName: 'Job No.',
      field: 'jobNo',
      width: 130,
      cellStyle: { color: '#1d4ed8', fontWeight: 700, fontFamily: 'monospace', display: 'flex', alignItems: 'center' },
    },
    {
      headerName: 'Project Name',
      field: 'projectName',
      width: 190,
      cellStyle: { color: '#374151', display: 'flex', alignItems: 'center' },
      tooltipField: 'projectName',
    },
    {
      headerName: 'Buildings',
      colId: 'buildings',
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => buildingNames(params.data),
      cellStyle: { color: '#475569', display: 'flex', alignItems: 'center' },
      tooltipValueGetter: (params) => buildingNames(params.data),
    },
    {
      headerName: 'Release',
      colId: 'release',
      width: 110,
      valueGetter: (params) => releaseMonths(params.data),
      cellStyle: { color: '#6b7280', textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' },
      headerClass: 'ag-right-header',
    },
  ], []);

  const gridHeight = useMemo(() => {
    const rowHeight = 35;    // matches AgGridTable default rowHeight
    const headerHeight = 100; // matches AgGridTable default headerHeight
    const visibleRows = Math.min(Math.max(projects.length, 1), 6);
    return `${visibleRows * rowHeight + headerHeight}px`;
  }, [projects.length]);
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [bodyEdited, setBodyEdited] = useState(false);
  const generatedBody = useRef('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Greets every recipient, e.g. "Dear MIH/NGD" for two To addresses.
  const recipientName = useMemo(
    () => recipientGreeting(toEmails, 'Production Team'),
    [toEmails]
  );

  useEffect(() => {
    setSubject(`3 MONTHS PRODUCTION FORECAST - ${confirmPeriod}`);
  }, [confirmPeriod]);

  // Regenerate the default body while it is untouched; once the user types into
  // the editor, adding a recipient must not throw their draft away.
  // Same target the backend template uses when no custom body is supplied
  // (PlanningMailer#url) — the Planning grid for the released period.
  const forecastUrl = useMemo(
    () => `${window.location.origin}/crm/release-forecast/planning${confirmPeriod ? `?period=${encodeURIComponent(confirmPeriod)}` : ''}`,
    [confirmPeriod]
  );

  useEffect(() => {
    if (bodyEdited) return;
    const generated = buildDefaultBody({
      period: confirmPeriod,
      recipient: recipientName,
      sender: fromName || 'Planning Team',
      url: forecastUrl,
    });
    generatedBody.current = generated;
    setBody(generated);
  }, [confirmPeriod, recipientName, fromName, forecastUrl, bodyEdited]);

  // TinyMCE echoes every programmatic setContent back through onEditorChange
  // (re-serialised, so whitespace differs) — only a body that no longer matches
  // the generated one counts as a real edit.
  const handleBodyChange = (value) => {
    if (squash(value) !== squash(generatedBody.current)) setBodyEdited(true);
    setBody(value);
  };

  useEffect(() => {
    dispatch(getPlanningEmailConfig({ target: 'production' }))
      .unwrap()
      .then((data) => {
        setToEmails(data.to_emails || []);
        setCcEmails(data.cc_emails || []);
        setFromEmail(data.from_email || '');
        setFromName(data.from_name || '');
        setReplyTo(data.reply_to || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dispatch]);

  const handleConfirm = async () => {
    if (!confirmPeriod || toEmails.length === 0 || !subject.trim() || !body.trim()) return;
    setSubmitting(true);
    try {
      await onConfirm(confirmPeriod, {
        to_emails: toEmails,
        cc_emails: ccEmails,
        subject: subject.trim(),
        body,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isInvalid = !confirmPeriod || toEmails.length === 0 || !subject.trim() || !body.trim();

  return (
    <div className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/50 tw-p-4" style={{ zIndex: 999999 }}>
      <div className="tw-flex tw-max-h-[92vh] tw-w-full tw-max-w-3xl tw-flex-col tw-overflow-hidden tw-rounded-lg tw-bg-white tw-shadow-2xl">
        <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-3 tw-bg-blue-600 tw-px-5 tw-py-4">
          <span className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/15">
            <FaEnvelope size={16} className="tw-text-white" />
          </span>
          <div>
            <h5 className="tw-mb-0 tw-text-sm tw-font-bold tw-text-white">Release Forecast to Production</h5>
            <p className="tw-mb-0 tw-text-xs tw-text-blue-100">Compose and confirm the release email</p>
          </div>
          <button type="button" onClick={onCancel} aria-label="Close" className="tw-ml-auto tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-border-0 tw-bg-white/10 tw-text-white hover:tw-bg-white/20">
            <FaTimes size={12} />
          </button>
        </div>

        <div className="tw-flex-1 tw-overflow-y-auto tw-p-4">
          {loading ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-20 tw-text-slate-400">
              <FaSpinner className="tw-animate-spin" />
              <span className="tw-text-sm">Loading email settings...</span>
            </div>
          ) : (
            <div className="tw-flex tw-flex-col tw-gap-3">
              <div className="tw-overflow-hidden tw-rounded-md tw-border tw-border-slate-200">
                <div className="tw-flex tw-items-center tw-gap-3 tw-border-b tw-border-slate-100 tw-bg-slate-50 tw-px-4 tw-py-2.5">
                  <FieldLabel icon={FaUser}>From</FieldLabel>
                  {/* Mail is sent from the portal address under the user's name;
                      replies go back to the user (ApplicationMailer#sender_as). */}
                  <div className="tw-min-w-0 tw-truncate tw-text-xs">
                    <span className="tw-font-semibold tw-text-slate-700">{fromName || 'Current user'}</span>
                    {fromEmail && <span className="tw-ml-2 tw-text-slate-400">&lt;{fromEmail}&gt;</span>}
                    {replyTo && <span className="tw-ml-2 tw-text-slate-400">· replies to {replyTo}</span>}
                  </div>
                </div>
                <div className="tw-flex tw-items-start tw-gap-3 tw-border-b tw-border-slate-100 tw-px-4 tw-py-2">
                  <FieldLabel icon={FaUsers}>To</FieldLabel>
                  <div className="tw-min-w-0 tw-flex-1">
                    <RecipientSelect value={toEmails} onChange={setToEmails} placeholder="Search employee or enter email..." />
                    {toEmails.length === 0 && <p className="tw-mb-0 tw-mt-1 tw-text-[10px] tw-text-red-500">At least one recipient is required.</p>}
                  </div>
                </div>
                <div className="tw-flex tw-items-start tw-gap-3 tw-border-b tw-border-slate-100 tw-px-4 tw-py-2">
                  <FieldLabel icon={FaUsers}>CC</FieldLabel>
                  <div className="tw-min-w-0 tw-flex-1">
                    <RecipientSelect value={ccEmails} onChange={setCcEmails} placeholder="Search employee or enter email..." />
                  </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-2.5">
                  <FieldLabel icon={MdSubject}>Subject</FieldLabel>
                  <input value={subject} onChange={(event) => setSubject(event.target.value)} className="tw-min-w-0 tw-flex-1 tw-border-0 tw-bg-transparent tw-text-xs tw-font-medium tw-text-slate-700 focus:tw-outline-none" />
                </div>
              </div>

              <div>
                <div className="tw-mb-1.5 tw-flex tw-items-center tw-gap-1.5">
                  <FaEdit size={11} className="tw-text-slate-400" />
                  <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-500">Email content</span>
                </div>
                <div className="tw-overflow-hidden tw-rounded-md tw-border tw-border-slate-200">
                  <TinymceEditor
                    value={body}
                    handleChange={handleBodyChange}
                    height="220px"
                    resizable
                    minHeight={160}
                    maxHeight={620}
                    initOverride={{ menubar: false, plugins: 'lists link', toolbar: 'bold italic underline | bullist numlist | link | removeformat' }}
                  />
                </div>
              </div>

              <div className="tw-overflow-hidden tw-rounded-md tw-border tw-border-slate-200">
                <button type="button" onClick={() => setDetailsOpen((open) => !open)} className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-border-0 tw-bg-slate-50 hover:tw-bg-slate-100 tw-px-4 tw-py-3 tw-text-left tw-transition-colors">
                  <span className="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded tw-bg-blue-100 tw-text-blue-600">
                    <FaListAlt size={13} />
                  </span>
                  <span className="tw-flex-1">
                    <span className="tw-block tw-text-xs tw-font-bold tw-text-slate-800">Release details</span>
                    <span className="tw-mt-0.5 tw-block tw-text-[11px] tw-text-slate-500">
                      {confirmPeriod} · {projectCount} project{projectCount !== 1 ? 's' : ''}
                      {buildingCount > 0 && ` · ${buildingCount} building${buildingCount !== 1 ? 's' : ''}`}
                    </span>
                  </span>
                  <span className="tw-text-slate-400">{detailsOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}</span>
                </button>
                {detailsOpen && (
                  <div className="tw-border-t tw-border-slate-200 tw-p-3">
                    <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2">
                      <FaCheckCircle className="tw-text-emerald-500" size={13} />
                      <span className="tw-text-xs tw-font-semibold tw-text-slate-700">
                        {projectCount} project{projectCount !== 1 ? 's' : ''} will be released
                      </span>
                    </div>
                    {projects.length === 0 ? (
                      <p className="tw-mb-0 tw-py-2 tw-text-xs tw-text-slate-400">No project to list.</p>
                    ) : (
                      <div className="tw-overflow-hidden tw-rounded tw-border tw-border-slate-200">
                        <AgGridTable
                          columnDefs={columnDefs}
                          rowData={projects}
                          height={gridHeight}
                          rowNumbers={true}
                          sort={true}
                          filter={false}
                          pagination={true}
                        />
                      </div>
                    )}
                    {record?.partialList && (
                      <p className="tw-mb-0 tw-mt-2 tw-rounded tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-[11px] tw-text-amber-700">
                        Listing the projects on the current page — the release covers every project of {confirmPeriod}.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="tw-flex tw-shrink-0 tw-justify-end tw-gap-2 tw-border-t tw-border-slate-200 tw-bg-white tw-px-4 tw-py-3">
          <Button variant="ghost" size="sm" className="tw-h-9" onClick={onCancel} disabled={submitting}><FaTimes size={11} /> Cancel</Button>
          <Button variant="success" size="sm" className="tw-h-9" onClick={handleConfirm} disabled={loading || isInvalid} loading={submitting}><FaPaperPlane size={11} /> Send &amp; Release</Button>
        </div>
      </div>
    </div>
  );
};

ProductionReleaseConfirmDialog.propTypes = {
  record: PropTypes.shape({
    period: PropTypes.string,
    recordCount: PropTypes.number,
    // Pending projects of the period, as loaded by the page's own filters.
    projects: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      jobNo: PropTypes.string,
      projectName: PropTypes.string,
      buildings: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        releaseMonths: PropTypes.arrayOf(PropTypes.string),
      })),
    })),
    // True when the page holds more rows than it has loaded.
    partialList: PropTypes.bool,
  }).isRequired,
  defaultPeriod: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default ProductionReleaseConfirmDialog;
